import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Chip } from 'primereact/chip';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import * as actionTypes from '../../../redux/actionTypes';
import { getLdapId } from '../../../../../common/Utils/msalUtils';
import '../TeamManager.scss';

/**
 * TeamManager Component
 *
 * Allows creating and removing teams via the backend API.
 * The user who creates a team becomes its ADMIN and can manage
 * which other user IDs have access to the team's rules and
 * batch-processing features.
 */
const TeamManager = () => {
  const dispatch = useDispatch();
  const toast = useRef(null);
  const currentUser = getLdapId() || 'SYSTEM';

  const { teamDetails, teamDetailsLoading, teamsLoading, error, successMessage, teams, teamRegistry } = useSelector(
    (state) => state.dataCategorization
  );

  // Normalize a user ID for comparison — strip email domain, lowercase
  const normalizeId = (id) => (id || '').toLowerCase().split('@')[0];
  const meNorm = normalizeId(currentUser);

  // When the backend hasn't returned data yet, synthesize team detail objects
  // from the locally-persisted teamRegistry + teams list so the UI still works.
  const effectiveTeams = (teamDetails && teamDetails.length > 0)
    ? teamDetails
    : (teams || []).map(t => ({
        id: t.value,
        name: t.label,
        value: t.value,
        createdBy: teamRegistry?.[t.value]?.createdBy || null,
        members: teamRegistry?.[t.value]?.members || [],
      }));

  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDescription, setNewTeamDescription] = useState('');
  const [managingTeam, setManagingTeam] = useState(null);
  const [newMemberInput, setNewMemberInput] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('MEMBER');

  // Load all teams on mount
  useEffect(() => {
    dispatch({ type: actionTypes.GET_ALL_TEAMS_DETAILED });
  }, [dispatch]);

  // Error / success toasts
  useEffect(() => {
    if (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: error, life: 5000 });
      dispatch({ type: actionTypes.CLEAR_DATA_CATEGORY_ERROR });
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (successMessage) {
      toast.current?.show({ severity: 'success', summary: 'Success', detail: successMessage, life: 3000 });
      setNewTeamName('');
      setNewTeamDescription('');
      dispatch({ type: actionTypes.CLEAR_SUCCESS_MESSAGE });
    }
  }, [successMessage, dispatch]);

  // Sync managingTeam with the latest data from the store (members update in real time)
  useEffect(() => {
    if (managingTeam) {
      const updated = effectiveTeams.find(t => t.id === managingTeam.id);
      if (updated) setManagingTeam(updated);
    }
  }, [teamDetails, teams, teamRegistry]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Team CRUD ──
  const handleCreateTeam = () => {
    const name = newTeamName.trim();
    if (!name) {
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Team name is required', life: 3000 });
      return;
    }
    dispatch({
      type: actionTypes.CREATE_TEAM,
      payload: { name, description: newTeamDescription.trim() },
    });
  };

  const handleDeleteTeam = (team) => {
    confirmDialog({
      message: `Delete team "${team.name}"? All members will lose access.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => dispatch({ type: actionTypes.DELETE_TEAM, payload: { teamId: team.id } }),
    });
  };

  // ── Member management ──
  const handleAddMember = () => {
    const uid = newMemberInput.trim();
    if (!uid || !managingTeam) return;
    dispatch({
      type: actionTypes.ADD_TEAM_MEMBER,
      payload: { teamId: managingTeam.id, memberData: { userId: uid, role: newMemberRole } },
    });
    setNewMemberInput('');
  };

  const handleRemoveMember = (teamId, memberUserId) => {
    confirmDialog({
      message: `Remove "${memberUserId}" from this team?`,
      header: 'Confirm Remove',
      icon: 'pi pi-user-minus',
      acceptClassName: 'p-button-danger',
      accept: () =>
        dispatch({ type: actionTypes.REMOVE_TEAM_MEMBER, payload: { teamId, memberUserId } }),
    });
  };

  // ── Helpers ──
  const getRoleForCurrentUser = (team) => {
    // Check teamRegistry first (works without a backend)
    const regKey = team.value || team.name;
    const regEntry = teamRegistry?.[regKey];
    if (regEntry) {
      if (normalizeId(regEntry.createdBy) === meNorm) return 'admin';
      const regMember = (regEntry.members || []).find(m => normalizeId(m.userId) === meNorm);
      if (regMember) return regMember.role === 'ADMIN' ? 'admin' : 'member';
    }
    // Fall back to API-backed team data (normalized comparison)
    if (normalizeId(team.createdBy) === meNorm) return 'admin';
    if (!team.members || team.members.length === 0) return 'open';
    const me = team.members.find(m => normalizeId(m.userId) === meNorm);
    if (!me) return 'open';
    return me.role === 'ADMIN' ? 'admin' : 'member';
  };

  const isAdmin = (team) => getRoleForCurrentUser(team) === 'admin';

  return (
    <div className="team-manager-container">
      <Toast ref={toast} />

      <Card className="team-manager-card">
        <div className="team-manager-header">
          <h3>
            <i className="pi pi-sitemap" style={{ marginRight: '8px' }} />
            Manage Teams
          </h3>
          <p className="subtitle">
            Create teams for data categorization. The creator becomes the team admin
            and controls who can manage rules and process batches for that team.
          </p>
        </div>

        {/* ── Create team form ── */}
        <div className="team-manager-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="team-name">Team Name *</label>
              <InputText
                id="team-name"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder="e.g. IT Support, Security"
                onKeyPress={(e) => { if (e.key === 'Enter') handleCreateTeam(); }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="team-desc">Description</label>
              <InputText
                id="team-desc"
                value={newTeamDescription}
                onChange={(e) => setNewTeamDescription(e.target.value)}
                placeholder="Optional description"
              />
            </div>
            <div className="form-group button-group">
              <Button
                label="Create Team"
                icon="pi pi-plus"
                onClick={handleCreateTeam}
                className="p-button-primary"
                disabled={!newTeamName.trim() || teamsLoading}
                loading={teamsLoading}
              />
            </div>
          </div>
        </div>

        {/* ── Team list ── */}
        <div className="team-manager-table">
          <h4 style={{ marginTop: '24px', marginBottom: '12px' }}>
            Current Teams ({effectiveTeams.length})
            {teamDetailsLoading && (
              <ProgressSpinner style={{ width: 20, height: 20, marginLeft: 8 }} strokeWidth="4" />
            )}
          </h4>

          {teamDetailsLoading && effectiveTeams.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              <ProgressSpinner style={{ width: 32, height: 32 }} />
              <p style={{ marginTop: 8 }}>Loading teams...</p>
            </div>
          ) : (
            <div className="team-palette">
              {effectiveTeams.map((team) => {
                const role = getRoleForCurrentUser(team);
                const memberCount = team.members?.length || 0;
                return (
                  <span
                    key={team.id}
                    className={`team-palette-item team-palette-item--${role}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    {role === 'admin' && (
                      <i className="pi pi-crown" title="You are the admin" style={{ color: '#f59e0b' }} />
                    )}
                    {role === 'member' && (
                      <i className="pi pi-user" title="You are a member" style={{ color: '#2563eb' }} />
                    )}
                    {role === 'open' && (
                      <i className="pi pi-lock-open" title="Open team" style={{ color: '#9ca3af' }} />
                    )}

                    <span style={{ fontWeight: 600 }}>{team.name}</span>

                    <Tag
                      value={`${memberCount} member${memberCount !== 1 ? 's' : ''}`}
                      severity="info"
                      style={{ fontSize: '0.7rem', padding: '2px 6px', marginLeft: 2 }}
                    />

                    {team.createdBy && (
                      <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                        by {team.createdBy}
                      </span>
                    )}

                    {/* Manage members — always visible */}
                    <Button
                      icon="pi pi-users"
                      className="p-button-rounded p-button-info p-button-sm"
                      onClick={() => { setManagingTeam(team); setNewMemberInput(''); }}
                      disabled={teamsLoading}
                      tooltip="Manage members"
                      tooltipOptions={{ position: 'top' }}
                      style={{ marginLeft: 4 }}
                      aria-label="Manage members"
                    />

                    {/* Delete — always visible */}
                    <Button
                      icon="pi pi-trash"
                      className="p-button-rounded p-button-danger p-button-sm team-delete-btn"
                      onClick={() => handleDeleteTeam(team)}
                      disabled={teamsLoading}
                      tooltip="Delete team"
                      tooltipOptions={{ position: 'top' }}
                      style={{ marginLeft: 2 }}
                      aria-label="Delete team"
                    />
                  </span>
                );
              })}
              {effectiveTeams.length === 0 && !teamDetailsLoading && (
                <span style={{ color: '#6c757d', fontSize: '13px' }}>
                  No teams yet. Create one above.
                </span>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* ── Member management dialog ── */}
      <Dialog
        header={
          <span>
            <i className="pi pi-users" style={{ marginRight: 8, color: '#2563eb' }} />
            Manage Members — {managingTeam?.name}
          </span>
        }
        visible={!!managingTeam}
        onHide={() => setManagingTeam(null)}
        style={{ width: '520px', maxWidth: '95vw' }}
        modal
      >
        {managingTeam && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

            {/* Admin info */}
            <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px' }}>
              <span style={{ fontSize: '0.78rem', color: '#92400e', fontWeight: 600 }}>
                <i className="pi pi-crown" style={{ color: '#f59e0b', marginRight: 5 }} />
                Team Admin
              </span>
              <div style={{ marginTop: 4, fontWeight: 600, fontSize: '0.92rem' }}>
                {managingTeam.createdBy || currentUser}
              </div>
              <div style={{ marginTop: 3, fontSize: '0.77rem', color: '#6b7280' }}>
                Only the admin can add/remove members and delete the team.
              </div>
            </div>

            {/* Current members */}
            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8, display: 'block' }}>
                Members ({(managingTeam.members || []).length})
              </label>
              {(managingTeam.members || []).length === 0 ? (
                <p style={{ color: '#9ca3af', fontSize: '0.85rem', margin: 0 }}>
                  No members yet. Added users can manage rules and process batches for this team.
                </p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(managingTeam.members || []).map((member) => (
                    <Chip
                      key={member.userId}
                      label={`${member.userId} (${member.role})`}
                      removable={member.role !== 'ADMIN'}
                      onRemove={() => handleRemoveMember(managingTeam.id, member.userId)}
                      style={{ fontSize: '0.82rem' }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Add member */}
            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 6, display: 'block' }}>
                Add Member by User ID
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <InputText
                  value={newMemberInput}
                  onChange={(e) => setNewMemberInput(e.target.value)}
                  placeholder="e.g. jdoe123"
                  onKeyPress={(e) => { if (e.key === 'Enter') handleAddMember(); }}
                  style={{ flex: 1 }}
                />
                <select
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole(e.target.value)}
                  style={{ padding: '0.5rem', borderRadius: 4, border: '1px solid #d1d5db', fontSize: '0.85rem' }}
                >
                  <option value="MEMBER">MEMBER</option>
                  <option value="ADMIN">ADMIN</option>
                </select>
                <Button
                  icon="pi pi-user-plus"
                  label="Add"
                  size="small"
                  onClick={handleAddMember}
                  disabled={!newMemberInput.trim() || teamsLoading}
                  loading={teamsLoading}
                />
              </div>
              <p style={{ marginTop: 6, fontSize: '0.78rem', color: '#6b7280', margin: '6px 0 0' }}>
                Enter the user's LDAP / network ID. ADMINs can manage members; MEMBERs can manage rules and batches.
              </p>
            </div>

          </div>
        )}
      </Dialog>
    </div>
  );
};

export default TeamManager;

